import { call, put } from 'typed-redux-saga';

import { isModerationCommandObject } from '../../../../../shared/features/moderation/helpers';
import { onModerationCommandReceiveSaga } from '../../../../../shared/features/moderation/sagas';
import { moderationRoleChanged } from '../../../../../shared/features/moderation/slice';
import { toLoggableError } from '../../../../../shared/helpers/errors';
import { createLogger } from '../../../../../shared/helpers/loggerFacade';
import { Emotion } from '../../../../../types';
import { isAccessLevelData } from '../../../helpers/is-access-level-data';
import { NotificationEvent, NotificationObject } from '../../../types';
import { broadcastStateChangeSaga } from '../../broadcast';
import { clientCreatedChatSaga } from '../../chat';
import { permissionsChangedSaga } from '../../permissions';
import { onEmotionSaga } from '../../reaction';
import { waitingRoomUpdateSaga } from '../../waiting-rooms';
import {
    isBroadcastDataPayload,
    isChatCreatedPayload,
    isConferenceStatePayload,
    isPassportAccountModificationPayload,
    isPeersStatePayload,
    isPermissionsModificationPayload,
    isRoleChangedPayload,
    isSummarizationSettingsUpdatePayload,
} from '../helpers/notifications-type-guards';
import { forceRequestStates } from '../slice';
import { broadcastAccessLevelChangeSaga, conferenceAccessLevelChangeSaga } from './access-level';
import { syncConferenceState } from './conference';
import { passportAccountModificationSaga, updatePeersInfoSaga } from './participants';

const logger = createLogger('goloom:notification');

export function* onNotificationSaga({ payload, notificationId }: NotificationEvent) {
    try {
        const notificationObj: NotificationObject = JSON.parse(payload);
        const notificationPayload = notificationObj.payload || notificationObj;

        if (notificationObj.type === 'emotion') {
            const { peer_id, emotion_type, show_deadline, duration } = notificationObj.payload;

            const emotion: Emotion = {
                peerId: peer_id,
                emotionType: emotion_type,
                showDeadline: show_deadline,
                duration,
            };
            logger.event('Notification emotion', {
                additional: {
                    method: 'onNotificationSaga',
                    notification_id: notificationId,
                    show_deadline,
                    duration,
                    emotion_type,
                },
            });
            yield* call(onEmotionSaga, emotion);
        }

        // Пришла ли нотификация с обновлением conference.state
        if (isConferenceStatePayload(notificationPayload)) {
            logger.event('Notification update_conference_state', {
                additional: {
                    method: 'onNotificationSaga',
                    notification_id: notificationId,
                    conference_version: notificationPayload.conference.version,
                },
            });
            yield* call(syncConferenceState, notificationPayload.conference);
        }

        // Пришла ли нотификация о команде модерации
        if (isModerationCommandObject(notificationPayload)) {
            logger.event('Notification moderation_command', {
                additional: {
                    method: 'onNotificationSaga',
                    notification_id: notificationId,
                    moderation_command: notificationPayload.command.type,
                    source: notificationPayload.source,
                },
            });
            return yield* call(onModerationCommandReceiveSaga, notificationPayload);
        }

        // Пришла ли нотификация о создании чата
        if (isChatCreatedPayload(notificationPayload)) {
            logger.event('Notification chat_created', {
                additional: {
                    method: 'onNotificationSaga',
                    notification_id: notificationId,
                    chat_path: notificationPayload.chat_path,
                },
            });
            return yield* call(clientCreatedChatSaga, notificationPayload?.chat_path);
        }

        // Пришла ли нотификации о смене прав в конференции
        if (isRoleChangedPayload(notificationPayload)) {
            logger.event('Notification role_changed', {
                additional: {
                    method: 'onNotificationSaga',
                    notification_id: notificationId,
                    source_uid: notificationPayload.source.uid,
                    source_type: notificationPayload.source.type,
                    new_role: {
                        peer_id: notificationPayload.event.peer_id,
                        user_role: notificationPayload.event.user_role,
                    },
                },
            });
            return yield* put(moderationRoleChanged(notificationPayload));
        }

        // Пришла ли нотификации об изменении доступности встречи/трансляции
        if (isAccessLevelData(notificationPayload)) {
            if (notificationPayload.conference) {
                logger.event('Notification conference_access_changed', {
                    additional: {
                        method: 'onNotificationSaga',
                        notification_id: notificationId,
                        access_level: notificationPayload.conference.access_level,
                    },
                });
                yield* call(conferenceAccessLevelChangeSaga, notificationPayload.conference.access_level);
            }

            if (notificationPayload.broadcast) {
                logger.event('Notification broadcast_access_changed', {
                    additional: {
                        method: 'onNotificationSaga',
                        notification_id: notificationId,
                        access_level: notificationPayload.broadcast.access_level,
                    },
                });
                yield* call(broadcastAccessLevelChangeSaga, notificationPayload.broadcast.access_level);
            }

            return;
        }

        // Пришла ли нотификация с обновлением информации о пирах
        if (isPeersStatePayload(notificationPayload)) {
            logger.event('Notification update_peers_state', {
                additional: {
                    method: 'onNotificationSaga',
                    notification_id: notificationId,
                    peers_count: notificationPayload.peers.length,
                },
            });
            return yield* call(updatePeersInfoSaga, notificationPayload.peers);
        }

        // Пришла ли нотификация о смена аккаунта в паспорте
        if (isPassportAccountModificationPayload(notificationPayload)) {
            logger.event('Notification passport_account_modification', {
                additional: {
                    method: 'onNotificationSaga',
                    notification_id: notificationId,
                },
            });

            return yield* call(passportAccountModificationSaga, notificationPayload.peer_id);
        }

        // Пришла ли нотификация об обновлении данных трансляции
        if (isBroadcastDataPayload(notificationPayload)) {
            logger.event('Notification update_broadcast_data', {
                additional: {
                    method: 'onNotificationSaga',
                    notification_id: notificationId,
                    broadcast_status: notificationPayload.broadcast_data.status,
                    stream_uri: notificationPayload.broadcast_data.stream_uri,
                    broadcast_uri: notificationPayload.broadcast_data.broadcast_uri,
                },
            });
            return yield* call(broadcastStateChangeSaga, notificationPayload.broadcast_data);
        }

        // Пришла ли нотификация об изменении прав участников
        if (isPermissionsModificationPayload(notificationObj)) {
            logger.event('Notification permissions_modification', {
                additional: {
                    method: 'onNotificationSaga',
                    notification_id: notificationId,
                },
            });
            return yield* call(permissionsChangedSaga);
        }

        if (isSummarizationSettingsUpdatePayload(notificationObj)) {
            return yield* put(forceRequestStates());
        }

        if (notificationObj.type === 'waiting_room_update') {
            logger.event('Notification waiting_room_update', {
                additional: {
                    method: 'onNotificationSaga',
                    notification_id: notificationId,
                },
            });
            return yield* call(waitingRoomUpdateSaga);
        }

        // Если не опознали нотификацию, то логируем warning.
        // Для опознания используем список ключей объекта, потому что нет одного единого поля
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        logger.warn(`Unknown notification payload with keys ${Object.keys(notificationObj).join(', ')}`);
    } catch (error) {
        logger.error(
            toLoggableError(error, {
                method: 'onNotificationSaga',
            })
        );
    }
}
